import { Button } from "@/components/Button";
import { useState } from "react";
import { SearchInput } from "@/components/SearchInput";
import { Modal } from "@/components/Modal";
import { Select } from "@/components/Select";
import { ListToolbar } from "@/components/ListToolbar";
import { useProviderContext } from "@/pages/Providers/useProviderApi";
import type { Catalogs, ProviderColumnFilter } from "@/pages/Providers/types";

interface ProviderFiltersProps {
    search: string;
    controls?: React.ReactNode;
    filters: Record<string, string>;
    catalogs?: Catalogs;
    columnFilters: ProviderColumnFilter[];
    onColumns: (filters: ProviderColumnFilter[]) => void;
    onSearch: (value: string) => void;
    onFilter: (name: string, value: string) => void;
    onClear: () => void;
}

const filterOptions: Record<string, string[]> = {
    state: ["active", "inactive", "all"],
    bc: ["all", "linked", "unlinked"],
    conflicts: ["all", "pending", "blocking", "warning"],
};
const catalogFilters = {
    provider_type_id: "types",
    speciality_id: "specialities",
    country_id: "countries",
    payment_method_id: "payment_methods",
    validation_state_id: "validation_states",
} as const;

export const ProviderFilters = ({
    search,
    controls,
    filters,
    catalogs,
    columnFilters,
    onColumns,
    onSearch,
    onFilter,
    onClear,
}: ProviderFiltersProps) => {
    const { t } = useProviderContext();
    const [open, setOpen] = useState(false);
    const updateColumn = (id: string, value: string): void =>
        onColumns([
            ...columnFilters.filter((filter) => filter.id !== id),
            ...(value === "all" ? [] : [{ id, value }]),
        ]);
    return (
        <div className="space-y-3">
            <ListToolbar
                search={
                    <SearchInput
                        value={search}
                        onChange={onSearch}
                        placeholder={t("providers.ui.search")}
                        aria-label={t("providers.ui.search")}
                    />
                }
                controls={
                    <div className="flex max-w-full flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            icon="filter"
                            onClick={() => setOpen(true)}
                        >
                            {t("providers.ui.filters")}
                        </Button>
                        {controls}
                    </div>
                }
            />
            <Modal
                isOpen={open}
                onClose={() => setOpen(false)}
                title={t("providers.ui.filters")}
                maxWidth="md"
                hideFullscreen
                footer={
                    <>
                        <Button variant="ghost" onClick={onClear}>
                            {t("providers.ui.clear_filters")}
                        </Button>
                        <Button onClick={() => setOpen(false)}>
                            {t("workspace.list.show_results")}
                        </Button>
                    </>
                }
            >
                <div className="mt-4 flex flex-col gap-4">
                    {Object.entries(filterOptions).map(([name, options]) => (
                        <Select<string>
                            key={name}
                            label={t(`providers.filters.${name}`)}
                            value={filters[name]}
                            options={options.map((option) => ({
                                value: option,
                                label: t(`providers.filters.${name}_${option}`),
                            }))}
                            onChange={(value: string) => onFilter(name, value)}
                        />
                    ))}
                    {catalogs &&
                        Object.entries(catalogFilters).map(
                            ([field, catalog]) => (
                                <Select<string>
                                    key={field}
                                    label={t(
                                        `providers.fields.${field === "speciality_id" ? "speciality_ids" : field}`
                                    )}
                                    value={String(
                                        columnFilters.find(
                                            (filter) => filter.id === field
                                        )?.value ?? "all"
                                    )}
                                    options={[
                                        {
                                            value: "all",
                                            label: t("providers.ui.all_values"),
                                        },
                                        ...catalogs[catalog].map((option) => ({
                                            value: String(option.id),
                                            label: option.name,
                                        })),
                                    ]}
                                    onChange={(value: string) =>
                                        updateColumn(field, value)
                                    }
                                    searchable
                                />
                            )
                        )}
                    <Select<string>
                        label={t("providers.fields.payment_blocked")}
                        value={String(
                            columnFilters.find(
                                (filter) => filter.id === "payment_blocked"
                            )?.value ?? "all"
                        )}
                        options={[
                            {
                                value: "all",
                                label: t("providers.ui.all_values"),
                            },
                            { value: "1", label: t("providers.ui.yes") },
                            { value: "0", label: t("providers.ui.no") },
                        ]}
                        onChange={(value: string) =>
                            updateColumn("payment_blocked", value)
                        }
                    />
                </div>
            </Modal>
            <div className="flex flex-wrap items-center gap-2 text-xs">
                {Object.entries(filters)
                    .filter(([, value]) => value !== "all")
                    .map(([name, value]) => (
                        <Button
                            key={name}
                            size="xs"
                            variant="outline"
                            onClick={() => onFilter(name, "all")}
                        >
                            {t(`providers.filters.${name}_${value}`)}
                        </Button>
                    ))}
                {columnFilters.map((filter) => (
                    <Button
                        key={filter.id}
                        size="xs"
                        variant="outline"
                        onClick={() => updateColumn(filter.id, "all")}
                    >
                        {t(
                            `providers.fields.${filter.id === "speciality_id" ? "speciality_ids" : filter.id}`
                        )}
                    </Button>
                ))}
                {(search ||
                    columnFilters.length > 0 ||
                    Object.values(filters).some(
                        (value) => value !== "all"
                    )) && (
                    <Button
                        size="xs"
                        variant="ghost"
                        icon="x"
                        onClick={onClear}
                    >
                        {t("providers.ui.clear_filters")}
                    </Button>
                )}
            </div>
        </div>
    );
};
